import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual, set, startCase } from 'lodash';
import BaseModal from '../../theme/modals/BaseModal';
import Input from '../../theme/forms/Input';
import SimpleCheckbox from '../../theme/forms/SimpleCheckbox';

import { lookupUsers } from '../../../store/calls';
import { updateApplicationData } from '../../../store/actions/applicationData';
import {
	addErrorAlert,
	addSuccessAlert,
} from '../../../store/calls/alertLoadingDispatch';
import api from '../../../store/api';
import { toggleGlobalLoader } from '../../../store/actions/session';
import validators from '../../../utils/forms/validators';
import isUserAdmin from '../../../utils/session/perms';

/**
 *
 *
 * existing permissions example:
 *
 *
 "permissions": [
 {
 "email": "ph@cleanfi.com",
 "permissions": [
 "edit_requirements"
 ]
 },
 {
 "email": "morgan.hartley@gmail.com",
 "permissions": [
 "view",
 "edit"
 ]
 }
 ]
 */

function UserPermissionsBox({
								email,
								permissionsAvailable,
								permissionsAssigned,
								expandable = false,
								className,
								loading,
								onPermissionsChange,
							}) {
	const [expanded, setExpanded] = React.useState(true);
	const [isHovered, setIsHovered] = React.useState(false);
	const permissionLabelMap = {
		upload_files: 'Upload Files Only',
		view: 'Viewer',
		edit: 'Applicant',
		edit_requirements: 'Admin',
		bid: 'Bidder',
	};

	return (
		<div className={className}>
			<div
				className={`is-flex is-justify-content-space-between ${
					expandable ? 'cursor-is-pointer' : ''
				} has-text-primary 
                ${isHovered ? 'has-text-weight-semibold' : ''}`}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				onClick={() => expandable && setExpanded(!expanded)}
			>
				<div className={`is-size-6 ${expanded && 'has-text-weight-semibold'}`}>
					{email}
				</div>
				{expandable && (
					<i
						className={`fas fa-chevron-${
							expanded ? 'up' : isHovered ? 'down' : 'right'
						}`}
					/>
				)}
			</div>
			{expanded && (
				<div className="option-container mt-1 mb-2">
					{permissionsAvailable.map((permission) => (
						<SimpleCheckbox
							key={permission}
							isChecked={permissionsAssigned.includes(permission)}
							label={permissionLabelMap[permission] || startCase(permission)}
							loading={loading}
							onChange={() => {
								const set = new Set(permissionsAssigned);
								if (set.has(permission)) set.delete(permission); else set.add(permission);
								onPermissionsChange(email, Array.from(set));
							}}
						/>
					))}
				</div>
			)}
		</div>
	);
}

function ApplicationPermissionsModal({
										 onClose,
										 visible = true,
										 permissionsAvailable,
										 existingPermissions,
									 }) {
	const [search, setSearch] = React.useState('');
	const [searchIsValidEmail, setSearchIsValidEmail] = React.useState(false);
	const [users, setUsers] = React.useState([]);
	const [error, setError] = React.useState('');
	const [currentPermissions, setCurrentPermissions] = React.useState(
		existingPermissions || []
	);
	const [userPermissionsLoading, setUserPermissionsLoading] = React.useState(
		[]
	); // an array of the emails that have their permissions being updated

	const dispatch = useDispatch();

	const applicationData = useSelector((state) => state.applicationData?.app);
	const user_is_admin = useSelector(
		(state) => state.user?.decodedTokendData?.is_admin
	);

	const applicationLink = `https://${window.location.host}/application/${applicationData.id}`;

	// TO-DO: UPDATE APPLICATION DATA IN REDUX STORE ON PERMISSIONS UPDATE

	useEffect(() => {
		if (validators.isEmail.validator(search)) {
			setSearchIsValidEmail(true);
		} else if (searchIsValidEmail) {
			setSearchIsValidEmail(false);
		}
	}, [search]);

	const updatePermissions = () => {
		toggleGlobalLoader(true);
		return api
			.post(`/app/update-permissions/${applicationData.id}/`, {
				permissions: currentPermissions,
			})
			.then((res) => {
				setCurrentPermissions(res.data);
				addSuccessAlert('Permissions updated');
				return res;
			})
			.catch((err) => {
				console.error(err);
				addErrorAlert('There was an error updating permissions');
				return err;
			})
			.then((res) => {
				toggleGlobalLoader(false);
				return res;
			});
	};

	const doLookup = (val) => {
		window.userLookupTimeout = setTimeout(() => {
			setError('');
			if (searchIsValidEmail || isUserAdmin()) {
				lookupUsers(val)
					.then((res) => {
						setUsers(res);
					})
					.catch(() => setError('There was an error looking up users'));
			} else {
				setUsers([]);
				setError('Please enter a valid email address');
			}
		}, 250);
	};

	const handleSearch = (val) => {
		setSearch(val);
		if (val.length < 3) {
			setError('');
			setUsers([]);
		} else {
			clearTimeout(window.userLookupTimeout);
			doLookup(val);
		}
	};

	const onPermissionsChange = (email, permissions) => {
		// determine if the email is already in the list
		let existingIdx = currentPermissions.findIndex(
			(permission) => permission.email === email
		);
		if (existingIdx === -1) {
			setCurrentPermissions([...currentPermissions, { email, permissions }]);
		} else {
			setCurrentPermissions(
				currentPermissions.map((permission) => {
					if (permission.email === email) {
						return { email, permissions };
					}
					return permission;
				})
			);
		}
	};

	const handleClose = () => {
		if (!isEqual(existingPermissions, currentPermissions)) {
			updatePermissions().then((res) => {
				dispatch(updateApplicationData({ permissions: res.data }));
				onClose();
			});
		} else {
			onClose();
		}
	};

	return (
		<BaseModal visible={visible} onClose={handleClose}>
			<div className="px-3">
				<p className="is-size-4 has-text-primary">Application Permissions</p>
				{!!currentPermissions.length && (
					<>
						<p className="mt-3">
							You may share this link directly with permissioned users:
						</p>
						<p>
							<span className="has-text-link ">{applicationLink}</span>
							<span
								className="has-text-gray is-underlined is-clickable ml-3"
								onClick={() => {
									navigator.clipboard
										.writeText(applicationLink)
										.then(() => addSuccessAlert('Link copied to clipboard'));
								}}
							>
								Copy Link
							</span>
						</p>
					</>
				)}
			</div>
			<div className="px-3">
				{!!currentPermissions.length && (
					<>
						<div className="divider" />
						<div className="columns is-multiline">
							{currentPermissions.map((permission) => (
								<UserPermissionsBox
									key={permission.email}
									email={permission.email}
									permissionsAvailable={permissionsAvailable}
									permissionsAssigned={permission.permissions}
									className="column is-half mt-3"
									onPermissionsChange={onPermissionsChange}
									loading={userPermissionsLoading.includes(permission.email)}
								/>
							))}
						</div>
					</>
				)}
				<div className="divider" />
				<div>
					<Input
						type="text"
						label={
							currentPermissions.length
								? 'Add another user'
								: 'Enter an email to add a user to this application'
						}
						subLabel="Type a user's email to look them up"
						value={search}
						onChange={handleSearch}
						placeholder="jon@cleanfi.com"
					/>
					<div className="">
						{!!error && (
							<div className="py-2 mt-2 is-flex is-justify-content-space-between">
								<div className="has-text-danger">{error}</div>
							</div>
						)}
						{!error && users.length === 0 && search.length > 2 && (
							<div className="py-2 mt-2 is-flex is-justify-content-space-between">
								<p>That user is not on CleanFi.</p>
								{/* <a className='ml-2 has-text-primary is-underlined'>Invite them to work on this application.</a> */}
							</div>
						)}

						{users
							.filter((user) => {
								return (
									(user.email || '').length &&
									!currentPermissions.map((p) => p.email).includes(user.email)
								);
							})
							.sort((a, b) => a.email.localeCompare(b.email))
							.map((user) => (
								<UserPermissionsBox
									key={user.email}
									{...{
										email: user.email,
										permissionsAvailable,
										permissionsAssigned: [],
										expandable: true, // setting false means pre-expanded
										className: 'py-1',
									}}
									loading={userPermissionsLoading.includes(user.email)}
									onPermissionsChange={onPermissionsChange}
								/>
							))}
					</div>
				</div>
				<div className="divider" />
				<div className="buttons mt-2 is-justify-content-flex-end">
					<button className="button is-primary" onClick={handleClose}>
						I'm done
					</button>
					<button className="button is-danger" onClick={onClose}>
						Cancel
					</button>
				</div>
			</div>
		</BaseModal>
	);
}

export default ApplicationPermissionsModal;
